import Auth from "@aws-amplify/auth";
import React, { useContext, useState } from "react";
import { Navbar, Nav, Container, Button, Dropdown, Image, Offcanvas } from "react-bootstrap";
import {
  FiHome,
  FiUpload,
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiUser,
  FiLogIn,
  FiFileText,
  FiShield,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import AppContext from "../context/app-context";
import useAuth from "../context/useAuth";
import ExpenseTrackingMetadataDB from "../storage/ExpenseTrackingMetadataDB";
import { delay } from "../utils/helperFunctions";
import LanguageSwitcher, { languages } from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import i18n from "./../i18n";
import { BsFileBarGraph, BsPeople } from "react-icons/bs";
import { FaCreditCard, FaFileImport, FaQuestion } from "react-icons/fa";

function Header() {
  const { t } = useTranslation();
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const { userProfile, setAppRefresh } = useContext(AppContext);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const MobileNavItem = ({ icon: Icon, text, onClick }) => (
    <Button variant="link" className="text-dark d-flex flex-column align-items-center" onClick={onClick}>
      <Icon size={20} />
      <small className="mt-1">{text}</small>
    </Button>
  );
  const handleMenuAction = (actionCallback) => {
    return () => {
      actionCallback();
      setShowMobileMenu(false);
    };
  };

  async function onSignOut() {
    Auth.signOut();
    ExpenseTrackingMetadataDB.deleteAllItems();
    await delay(1);
    setAppRefresh(Math.random());
  }

  const STATIC_CONFIG = [
    {
      icon: FaFileImport,
      path: "/bulk",
      translationKey: "expenseTracking.bulkAdd",
    },
    {
      icon: FaCreditCard,
      path: "/cards",
      translationKey: "CreditCardPage.title",
    },
    {
      icon: FiFileText,
      path: "/terms",
      translationKey: "header.terms",
    },
    {
      icon: FiShield,
      path: "/privacy",
      translationKey: "header.privacy",
    },
    {
      icon: BsPeople,
      path: "/aboutus",
      translationKey: "header.aboutus",
    },
    {
      icon: FaQuestion,
      path: "faq",
      translationKey: "faq.title",
    },
  ];

  const SettingsDropdown = () => (
    <Dropdown align="end">
      <Dropdown.Toggle variant="light" className="d-flex align-items-center">
        <FiSettings className="me-2" />
        {t("header.settings")}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {currentUser && (
          <>
            <Dropdown.Item onClick={() => navigate("/profile")}>
              <FiUser className="me-2" /> {t("header.account_setting")}
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}

        {STATIC_CONFIG.map((item) => {
          const { icon: ICON_ELE, path, translationKey } = item;
          return (
            <Dropdown.Item key={path} onClick={() => navigate(path)}>
              <ICON_ELE className="me-2" /> {t(translationKey)}
            </Dropdown.Item>
          );
        })}
        {currentUser ? (
          <Dropdown.Item onClick={onSignOut}>
            <FiLogOut className="me-2" /> {t("header.signOut")}
          </Dropdown.Item>
        ) : (
          <Dropdown.Item onClick={() => navigate(`/login?redirect=${window.location.pathname}`)}>
            <FiLogIn className="me-2" /> {t("header.login")}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <>
      {/* Desktop Header */}
      <Navbar bg="white" expand="lg" className="shadow-sm d-none d-lg-block">
        <Container fluid="xl">
          <Navbar.Brand onClick={() => navigate("/")} className="cursor-pointer">
            <img src="/apple-touch-icon.png" alt="Logo" width="40" height="40" className="d-inline-block align-top" />
          </Navbar.Brand>

          <Nav className="mx-auto">
            <Nav.Link onClick={() => navigate("/")} className="d-flex align-items-center">
              <FiHome className="me-2" /> {t("header.home")}
            </Nav.Link>
            <Nav.Link onClick={() => navigate("/upload")} className="d-flex align-items-center">
              <FiUpload className="me-2" /> {t("header.upload")}
            </Nav.Link>
            <Nav.Link onClick={() => navigate("/ExpenseTracking")} className="d-flex align-items-center">
              <FiDollarSign className="me-2" /> {t("header.expenses")}
            </Nav.Link>
            <Nav.Link onClick={() => navigate("/graphs")} className="d-flex align-items-center">
              <BsFileBarGraph className="me-2" /> {t("header.graphs")}
            </Nav.Link>
          </Nav>

          <Nav>
            <LanguageSwitcher />
            <SettingsDropdown />
          </Nav>
        </Container>
      </Navbar>
      {/* Mobile Header */}
      <Navbar bg="white" fixed="bottom" className="d-lg-none border-top">
        <Container className="justify-content-around">
          <MobileNavItem icon={FiHome} text={t("header.home")} onClick={() => navigate("/")} />
          <MobileNavItem icon={FiUpload} text={t("header.upload")} onClick={() => navigate("/upload")} />
          <MobileNavItem icon={FiDollarSign} text={t("header.expenses")} onClick={() => navigate("/ExpenseTracking")} />
          <MobileNavItem icon={BsFileBarGraph} text={t("header.graphs")} onClick={() => navigate("/graphs")} />
          <MobileNavItem icon={FiSettings} text={t("header.settings")} onClick={() => setShowMobileMenu(true)} />
        </Container>
      </Navbar>
      {/* Mobile Menu Offcanvas */}
      <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{t("header.settings")}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">
            {currentUser && (
              <Nav.Link onClick={handleMenuAction(() => navigate("/profile"))}>
                <FiUser className="me-2" /> {t("header.account_setting")}
              </Nav.Link>
            )}
            {STATIC_CONFIG.map((item) => {
              const { icon: ICON_ELE, path, translationKey } = item;
              return (
                <Nav.Link key={path} onClick={handleMenuAction(() => navigate(path))}>
                  <ICON_ELE className="me-2" /> {t(translationKey)}
                </Nav.Link>
              );
            })}

            <div className="px-3 py-2">
              <h6 className="text-muted small mb-2">{t("header.language")}</h6>
              <div className="d-flex gap-2 flex-wrap">
                {Object.entries(languages).map(([code, lang]) => (
                  <Button
                    key={code}
                    variant={i18n.language === code ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => {
                      i18n.changeLanguage(code);
                      // Don't close menu for language selection
                    }}
                  >
                    {lang.flag} {lang.name}
                  </Button>
                ))}
              </div>
            </div>

            {currentUser ? (
              <Nav.Link onClick={handleMenuAction(onSignOut)}>
                <FiLogOut className="me-2" /> {t("header.signOut")}
              </Nav.Link>
            ) : (
              <Nav.Link onClick={handleMenuAction(() => navigate("/login"))}>
                <FiLogIn className="me-2" /> {t("header.login")}
              </Nav.Link>
            )}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default Header;
